import React, { useState, useCallback, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import { api, apiClient } from '../utils/api';
import MalawiMapOnly from '../components/MalawiMapOnly';

type StatusTab = 'all' | 'resolved' | 'investigating' | 'reported';

// Bar Chart Component
interface IncidentBarChartProps {
  resolved: number;
  investigating: number;
  reported: number;
  loading?: boolean;
  category?: string;
  selectedDistrict?: string | null;
}

function IncidentBarChart({ resolved, investigating, reported, category = '', selectedDistrict = null, loading = false }: IncidentBarChartProps) {
  const total = resolved + investigating + reported;
  const maxCount = Math.max(resolved, investigating, reported, 1);

  // Calculate percentage of each status
  const getBarHeight = (count: number) => {
    // Use fixed maximum height with gradual scaling
    const heightPercentage = Math.round((count / maxCount) * 100);
    // Ensure bars have visual impact even with small values
    return `${Math.max(heightPercentage * 1.8, 15)}px`; // 180px max height, minimum 15px
  };

  // Calculate percentage of total for each status
  const getPercentage = (count: number) => {
    return total > 0 ? Math.round((count / total) * 100) : 0;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-60">
        <div className="text-center">
          <div className="w-10 h-10 mx-auto border-4 border-blue-200 rounded-full border-t-blue-500 animate-spin"></div>
          <p className="mt-2 text-sm font-medium text-gray-500">Loading statistics...</p>
        </div>
      </div>
    );
  } return (
    <div className="p-4 sm:p-6 lg:p-8 bg-white/50 backdrop-blur-sm">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 text-blue-600 bg-blue-100 rounded-lg">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-800 xl:text-2xl">Incidents by Status</h3>
            <p className="text-sm text-gray-500">{decodeURIComponent(category)} {selectedDistrict && `in ${selectedDistrict}`}</p>
          </div>
        </div>
        <div className="px-4 py-2 text-sm font-medium text-blue-800 bg-blue-100 rounded-lg">
          Total: <span className="font-bold">{total}</span> incidents
        </div>
      </div>

      <div className="flex justify-around px-4 pt-4 pb-12 sm:px-8 lg:px-12">
        {/* Resolved Bar */}
        <div className="flex flex-col items-center group">
          <div className="flex flex-col items-center mb-6 transition-transform group-hover:scale-105">
            <div className="text-4xl font-bold text-gray-800 xl:text-5xl">{resolved}</div>
            <div className="text-base font-medium text-gray-500">{getPercentage(resolved)}%</div>
          </div>
          <div className="flex flex-col justify-end h-[200px]">
            <div className="relative w-24 overflow-hidden bg-gray-100 md:w-32 lg:w-36 rounded-2xl">
              <div
                className="w-24 transition-all duration-1000 ease-out shadow-lg md:w-32 lg:w-36 bg-gradient-to-t from-green-600 to-green-400 shadow-green-200"
                style={{
                  height: getBarHeight(resolved),
                  transitionDelay: '100ms',
                }}
              ></div>
            </div>
          </div>
          <div className="flex items-center gap-2 mt-6">
            <div className="w-4 h-4 bg-green-500 rounded-full"></div>
            <div className="text-base font-medium text-gray-700">Resolved</div>
          </div>
        </div>

        {/* Investigating Bar */}
        <div className="flex flex-col items-center group">
          <div className="flex flex-col items-center mb-6 transition-transform group-hover:scale-105">
            <div className="text-4xl font-bold text-gray-800 xl:text-5xl">{investigating}</div>
            <div className="text-base font-medium text-gray-500">{getPercentage(investigating)}%</div>
          </div>
          <div className="flex flex-col justify-end h-[200px]">
            <div className="relative w-24 overflow-hidden bg-gray-100 md:w-32 lg:w-36 rounded-2xl">
              <div
                className="w-24 transition-all duration-1000 ease-out shadow-lg md:w-32 lg:w-36 bg-gradient-to-t from-amber-600 to-amber-400 shadow-amber-200"
                style={{
                  height: getBarHeight(investigating),
                  transitionDelay: '200ms',
                }}
              ></div>
            </div>
          </div>
          <div className="flex items-center gap-2 mt-6">
            <div className="w-4 h-4 rounded-full bg-amber-500"></div>
            <div className="text-base font-medium text-gray-700">Investigating</div>
          </div>
        </div>

        {/* Reported Bar */}
        <div className="flex flex-col items-center group">
          <div className="flex flex-col items-center mb-6 transition-transform group-hover:scale-105">
            <div className="text-4xl font-bold text-gray-800 xl:text-5xl">{reported}</div>
            <div className="text-base font-medium text-gray-500">{getPercentage(reported)}%</div>
          </div>
          <div className="flex flex-col justify-end h-[200px]">
            <div className="relative w-24 overflow-hidden bg-gray-100 md:w-32 lg:w-36 rounded-2xl">
              <div
                className="w-24 transition-all duration-1000 ease-out shadow-lg md:w-32 lg:w-36 bg-gradient-to-t from-blue-600 to-blue-400 shadow-blue-200"
                style={{
                  height: getBarHeight(reported),
                  transitionDelay: '300ms',
                }}
              ></div>
            </div>
          </div>
          <div className="flex items-center gap-2 mt-6">
            <div className="w-4 h-4 bg-blue-500 rounded-full"></div>
            <div className="text-base font-medium text-gray-700">Reported</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function CategoryIncidents() {
  const { category = '' } = useParams<{ category: string }>();
  const [selectedDistrict, setSelectedDistrict] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<StatusTab>('all');

  const { data: mapData, isLoading: mapLoading } = useQuery(
    ['incidents-map-category', category, selectedDistrict],
    () => api.dashboard.getIncidentsMap({ category, district: selectedDistrict }),
    { enabled: !!category }
  );

  const { data: listData, isLoading: listLoading } = useQuery(
    ['incidents-list-category', category, selectedDistrict, activeTab],
    () => apiClient.get('/incidents', {
      params: {
        limit: 1000,
        page: 1,
        category,
        district: selectedDistrict,
        status: activeTab !== 'all' ? activeTab : undefined
      }
    }),
    { enabled: !!category }
  );

  const incidents = listData?.data?.data || [];

  // Filter incidents by status (in case we need to filter client-side)
  const resolvedIncidents = activeTab === 'all' ? incidents.filter((inc: any) => inc.status === 'resolved') : incidents;
  const reportedIncidents = activeTab === 'all' ? incidents.filter((inc: any) => inc.status === 'reported') : incidents;
  const investigatingIncidents = activeTab === 'all' ? incidents.filter((inc: any) => inc.status === 'investigating') : incidents;

  // Handler for district selection from the map
  const handleDistrictClick = useCallback((districtName: string) => {
    setSelectedDistrict(prevDistrict =>
      prevDistrict === districtName ? null : districtName
    );
  }, []);

  // Render an incident item
  const renderIncident = (i: any) => {
    const statusColors = {
      resolved: 'border-l-4 border-green-500',
      investigating: 'border-l-4 border-amber-500',
      reported: 'border-l-4 border-blue-500'
    };

    const statusIcons = {
      resolved: (
        <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      investigating: (
        <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      ),
      reported: (
        <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
      )
    };

    const severityColors = {
      high: 'bg-red-100 text-red-800 border-red-200',
      medium: 'bg-amber-100 text-amber-800 border-amber-200',
      low: 'bg-green-100 text-green-800 border-green-200'
    };

    const severityColor = (i.severity?.toLowerCase() === 'high' || i.severity?.toLowerCase() === 'medium' || i.severity?.toLowerCase() === 'low')
      ? severityColors[i.severity.toLowerCase() as keyof typeof severityColors]
      : 'bg-gray-100 text-gray-800 border-gray-200';

    // Format date to be more readable
    const formatDate = (dateString: string) => {
      const date = new Date(dateString);
      const now = new Date();
      const diffInDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

      if (diffInDays === 0) {
        return `Today at ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
      } else if (diffInDays === 1) {
        return `Yesterday at ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
      } else if (diffInDays < 7) {
        return `${diffInDays} days ago`;
      } else {
        return date.toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' });
      }
    };

    return (
      <div key={i.id} className={`p-5 transition-all hover:bg-gray-50 ${statusColors[i.status as keyof typeof statusColors] || ''}`}>
        <div className="flex items-center justify-between">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`text-xs px-2 py-1 rounded border ${severityColor}`}>
              {i.severity}
            </span>
            <span className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${i.status === 'resolved' ? 'bg-green-100 text-green-800 border border-green-200' :
              i.status === 'investigating' ? 'bg-amber-100 text-amber-800 border border-amber-200' :
                'bg-blue-100 text-blue-800 border border-blue-200'
              }`}>
              {statusIcons[i.status as keyof typeof statusIcons]}
              {i.status}
            </span>
          </div>
          <span className="text-xs font-medium text-gray-500">{formatDate(i.created_at)}</span>
        </div>

        <div className="mt-2 text-lg font-semibold text-gray-800 xl:text-xl">{i.title || i.category}</div>

        {i.description && (
          <div className="mt-2 text-sm text-gray-600 xl:text-base">
            {i.description.length > 100
              ? `${i.description.substring(0, 100)}...`
              : i.description}
          </div>
        )}

        <div className="flex items-center mt-3 text-xs text-gray-500 xl:text-sm">
          <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          {i.district} • {i.constituency} • {i.ward}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container p-4 mx-auto space-y-6 sm:p-6 lg:p-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 text-white bg-blue-600 rounded-lg">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-800 xl:text-3xl">
              Incidents • <span className="text-blue-600">{decodeURIComponent(category)}</span>
              {selectedDistrict && <span className="ml-2 text-lg text-gray-600">• {selectedDistrict}</span>}
            </h1>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {selectedDistrict && (
              <button
                onClick={() => setSelectedDistrict(null)}
                className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-red-600 transition bg-red-50 border border-red-100 rounded-lg hover:bg-red-100"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                Clear district filter
              </button>
            )}
            <Link
              to="/admin/incidents-dashboard"
              className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-blue-600 transition bg-blue-50 border border-blue-100 rounded-lg hover:bg-blue-100"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to all incidents
            </Link>
          </div>
        </div>

        {/* Incident Statistics Bar Chart */}
        <div className="overflow-hidden bg-white border shadow-lg rounded-xl">
          <IncidentBarChart
            resolved={resolvedIncidents.length}
            investigating={investigatingIncidents.length}
            reported={reportedIncidents.length}
            category={category}
            selectedDistrict={selectedDistrict}
            loading={listLoading}
          />
        </div>

        <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
          <div className="overflow-hidden bg-white border shadow-lg rounded-xl">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <div className="flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <h3 className="font-bold text-gray-800">Geographic Distribution {selectedDistrict && `• ${selectedDistrict}`}</h3>
              </div>
              <div className="text-xs font-medium text-gray-500">
                Click on a district to filter
              </div>
            </div>
            <div className="p-4">
              {mapLoading ? (
                <div className="flex items-center justify-center h-[480px]">
                  <div className="text-center">
                    <div className="w-10 h-10 mx-auto border-4 border-blue-200 rounded-full border-t-blue-500 animate-spin"></div>
                    <p className="mt-3 text-sm font-medium text-gray-500">Loading map data...</p>
                  </div>
                </div>
              ) : (
                <MalawiMapOnly
                  incidentData={mapData?.data}
                  height={480}
                  onDistrictClick={handleDistrictClick}
                />
              )}
            </div>
          </div>

          <div className="overflow-hidden bg-white border shadow-lg rounded-xl">
            {/* Horizontal Tabs at the top */}
            <div className="border-b">
              <div className="flex">
                <button
                  className={`px-4 py-4 font-semibold flex-1 flex items-center justify-center gap-2 transition ${activeTab === 'all' ? 'border-b-2 border-blue-500 text-blue-700 bg-blue-50' : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'}`}
                  onClick={() => setActiveTab('all')}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                  </svg>
                  <span>All <span className="rounded-full bg-gray-200 px-2 py-0.5 text-xs ml-1">{incidents.length}</span></span>
                </button>
                <button
                  className={`px-4 py-4 font-semibold flex-1 flex items-center justify-center gap-2 transition ${activeTab === 'resolved' ? 'border-b-2 border-green-500 text-green-700 bg-green-50' : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'}`}
                  onClick={() => setActiveTab('resolved')}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>Resolved <span className="rounded-full bg-green-200 px-2 py-0.5 text-xs ml-1">{activeTab === 'resolved' ? incidents.length : resolvedIncidents.length}</span></span>
                </button>
                <button
                  className={`px-4 py-4 font-semibold flex-1 flex items-center justify-center gap-2 transition ${activeTab === 'investigating' ? 'border-b-2 border-amber-500 text-amber-700 bg-amber-50' : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'}`}
                  onClick={() => setActiveTab('investigating')}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <span>Investigating <span className="rounded-full bg-amber-200 px-2 py-0.5 text-xs ml-1">{activeTab === 'investigating' ? incidents.length : investigatingIncidents.length}</span></span>
                </button>
                <button
                  className={`px-4 py-4 font-semibold flex-1 flex items-center justify-center gap-2 transition ${activeTab === 'reported' ? 'border-b-2 border-blue-500 text-blue-700 bg-blue-50' : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'}`}
                  onClick={() => setActiveTab('reported')}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                  <span>Reported <span className="rounded-full bg-blue-200 px-2 py-0.5 text-xs ml-1">{activeTab === 'reported' ? incidents.length : reportedIncidents.length}</span></span>
                </button>
              </div>
            </div>

            {/* Tab Content - Single Table with Dynamic Content */}
            <div className="divide-y max-h-[520px] overflow-auto">
              {listLoading ? (
                <div className="flex items-center justify-center p-12">
                  <div className="text-center">
                    <div className="w-12 h-12 mx-auto border-4 border-blue-200 rounded-full border-t-blue-500 animate-spin"></div>
                    <p className="mt-3 text-sm font-medium text-gray-600">Loading incidents...</p>
                  </div>
                </div>
              ) : incidents.length === 0 ? (
                <div className="flex flex-col items-center justify-center p-12 text-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-16 h-16 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M12 14h.01M20 12a8 8 0 11-16 0 8 8 0 0116 0z" />
                  </svg>
                  <p className="mt-4 text-lg font-medium text-gray-600">
                    No {activeTab !== 'all' ? `${activeTab} ` : ''}incidents found
                  </p>
                  <p className="mt-2 text-sm text-gray-500">
                    {selectedDistrict
                      ? `No ${activeTab !== 'all' ? `${activeTab} ` : ''}incidents reported in ${selectedDistrict}`
                      : `There are no ${activeTab !== 'all' ? `${activeTab} ` : ''}incidents to display for this category`}
                  </p>
                </div>
              ) : (
                incidents.map(renderIncident)
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}